<script setup>
import { computed } from 'vue';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import MessagePreview from './MessagePreview.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import UnreadBadge from 'dashboard/components-next/Conversation/ConversationCard/UnreadBadge.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  // 定制: 新会话刚进来时一次性闪烁(由 ConversationItem 传入)
  flashNew: { type: Boolean, default: false },
});

defineEmits(['click', 'contextmenu']);

// 定制(2026-09-05): 头像已去掉, 悬停选择(hovered/Checkbox)随之移除

// 定制: 收件箱创建者(归属客服)名, 按 inbox.created_by_id 从 agents store 解析
const agents = useMapGetter('agents/getAgents');
const inboxCreator = computed(() => {
  const cid = props.inbox?.created_by_id;
  if (!cid || !agents.value) return '';
  const list = Array.isArray(agents.value)
    ? agents.value
    : Object.values(agents.value || {});
  return list.find(a => a?.id === cid)?.name || '';
});
// 定制: 创建者 badge 按名字哈希取色, 每人颜色稳定;
// 柔和 100底/700字(同名片/尘锋风格), 避开绿/橙(不与状态badge撞色) → 不突兀。
// 用内联样式(Tailwind 不会编译动态类名, 内联保证一定生效)。
const CREATOR_COLORS = [
  { bg: '#dbeafe', text: '#1d4ed8' }, // blue
  { bg: '#ede9fe', text: '#6d28d9' }, // purple
  { bg: '#fce7f3', text: '#be185d' }, // pink
  { bg: '#ccfbf1', text: '#0f766e' }, // teal
  { bg: '#e0e7ff', text: '#4338ca' }, // indigo
  { bg: '#ffe4e6', text: '#be123c' }, // rose
  { bg: '#cffafe', text: '#0e7490' }, // cyan
  { bg: '#fef3c7', text: '#b45309' }, // amber
];
const creatorStyle = computed(() => {
  const name = inboxCreator.value;
  if (!name) return '';
  // 显式映射: 给每个客服固定不同颜色(避免哈希撞色); 未知名字才用哈希兜底
  const CREATOR_COLOR_MAP = {
    杨洋: 0,
    李节恒: 1,
    王瑞雪: 2,
    张现荣: 3,
    张蒙蒙: 4,
    唐璐: 5,
    张艳春: 6,
    admin: 7,
  };
  let idx = CREATOR_COLOR_MAP[name];
  if (idx === undefined) {
    let h = 0;
    for (let i = 0; i < name.length; i += 1)
      h = (h * 31 + name.charCodeAt(i)) >>> 0;
    idx = h % CREATOR_COLORS.length;
  }
  const c = CREATOR_COLORS[idx];
  return `background:${c.bg};color:${c.text}`;
});

const unreadCount = computed(() => props.chat.unread_count);
const hasUnread = computed(() => unreadCount.value > 0);
// 定制: 新访客未回复 → 闪烁。最后一条真实消息是访客发的(message_type=0)即未回复。
const isUnreplied = computed(() => {
  const lm = props.chat?.last_non_activity_message;
  return !!lm && lm.message_type === 0;
});
// 定制: 会话列表 badge — 已存名片(有 phone/email/wechat)绿色 + 已推送尘锋(pushed_to_dustess)橙色
const contactCustomAttributes = computed(
  () =>
    props.currentContact?.custom_attributes ||
    props.currentContact?.customAttributes ||
    {}
);
// 定制: 访客名称 = 地域｜归属客服(同名片管理 ContactsList#visitorName 规则);
// 会话列表 contact 是 snake_case 原始对象, 双兼容 camel。
const contactAdditionalAttributes = computed(
  () =>
    props.currentContact?.additional_attributes ||
    props.currentContact?.additionalAttributes ||
    {}
);
const visitorName = computed(() => {
  const attrs = contactAdditionalAttributes.value || {};
  const region = [attrs.province, attrs.city]
    .map(v => (v || '').trim())
    .filter(Boolean)
    .filter(v => v !== 'XX')
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .join('');
  // 定制: 境外/港澳台IP拿不到省市(city=XX)时降级显示国家(如 美国/中国香港); 大陆的"中国"是噪音不显示
  const country =
    region || !(attrs.country || '').trim() || attrs.country.trim() === '中国'
      ? ''
      : attrs.country.trim();
  const place = region || country;
  const owner = (contactCustomAttributes.value?.owner || '').trim();
  const name = place && owner ? `${place}｜${owner}` : place || owner;
  // 拿不到地域和归属时回退成访客id本身, 与标题重复, 不显示
  return name && name !== props.currentContact?.name ? name : '';
});
const hasContactInfo = computed(() => {
  const c = props.currentContact || {};
  return !!(
    c.phone_number ||
    c.phoneNumber ||
    c.email ||
    contactCustomAttributes.value?.wechat ||
    contactCustomAttributes.value?.landline
  );
});
const pushedToDustess = computed(
  () =>
    !!(
      contactCustomAttributes.value?.pushed_to_dustess ||
      contactCustomAttributes.value?.pushedToDustess
    )
);
const lastMessageInChat = computed(() => getLastMessage(props.chat));

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const showMetaSection = computed(() => {
  return (
    props.showInboxName ||
    (props.showAssignee && props.assignee.name) ||
    props.chat.priority
  );
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 cursor-pointer conversation border-b border-n-slate-3 hover:border-n-surface-1 hover:bg-n-slate-3 dark:hover:bg-n-slate-7 group hover:z-[1] before:content-[none] before:absolute before:-top-px before:inset-x-0 before:h-px before:bg-n-surface-1 before:pointer-events-none hover:before:content-['']"
    :class="{
      'active animate-card-select bg-n-slate-4 dark:bg-n-slate-8 !border-n-surface-1':
        isActiveChat,
      'selected bg-n-slate-3 dark:bg-n-slate-7 !border-n-surface-1': selected,
      'px-0': compact,
      'px-3': !compact,
      'animate-conv-flash': isUnreplied,
      'animate-conv-flash-new': flashNew,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <!-- 定制: 去掉访客头像, 腾出宽度给访客id+访客名称 -->
    <div class="px-0 py-3 flex-1 min-w-0 border-line">
      <div
        v-if="showMetaSection || hasContactInfo || pushedToDustess || visitorName"
        class="flex items-center min-w-0 gap-1"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <!-- 定制: 访客名称(地域｜归属客服)放meta行最前(原收件箱名位置), 品牌蓝加粗 -->
        <span
          v-if="visitorName"
          class="mr-1 text-[13px] font-semibold text-n-brand min-w-0 truncate"
          :title="visitorName"
          >{{ visitorName }}</span
        >
        <div
          v-if="hasContactInfo || pushedToDustess"
          class="flex items-center gap-1 flex-shrink-0"
        >
          <span
            v-if="hasContactInfo"
            class="px-1.5 py-0.5 text-xxs font-medium rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
          >
            名片
          </span>
          <span
            v-if="pushedToDustess"
            class="px-1.5 py-0.5 text-xxs font-medium rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300"
          >
            尘锋
          </span>
        </div>
        <!-- 定制: 收件箱创建者(归属客服), 尘锋后右对齐, 按人取柔和色(避开绿/橙), 内联样式 -->
        <span
          v-if="inboxCreator"
          class="ml-auto inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xxs font-medium rounded"
          :style="creatorStyle"
        >
          <fluent-icon icon="person" size="10" />
          {{ inboxCreator }}
        </span>
        <div
          class="flex items-baseline gap-2 flex-shrink-0"
          :class="{
            'flex-1 justify-between': !showInboxName,
          }"
        >
          <span
            v-if="showAssignee && assignee.name"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center truncate"
          >
            <fluent-icon icon="person" size="12" class="text-n-slate-11" />
            {{ assignee.name }}
          </span>
          <CardPriorityIcon
            :priority="chat.priority"
            class="flex-shrink-0 !size-3.5"
          />
        </div>
      </div>
      <h4
        class="conversation--user text-sm my-0 mx-2 capitalize pt-0.5 text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0 ltr:pr-16 rtl:pl-16 text-n-slate-12 font-normal"
      >
        {{ currentContact.name }}
      </h4>
      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        class="my-0 mx-2 leading-6 h-6 flex-1 min-w-0 text-sm"
        :class="messagePreviewClass"
      />
      <p
        v-else
        key="no-messages"
        class="text-n-slate-11 text-sm my-0 mx-2 leading-6 h-6 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        class="absolute flex flex-col ltr:right-3 rtl:left-3"
        :class="showMetaSection ? 'top-8' : 'top-4'"
      >
        <span class="ml-auto font-normal leading-4 text-xxs">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :conversation-id="chat.id"
          />
        </span>
        <UnreadBadge
          v-if="hasUnread"
          :count="unreadCount"
          class="ltr:ml-auto rtl:mr-auto mt-1"
        />
      </div>
      <CardLabels
        v-if="showLabelsSection"
        :conversation-labels="chat.labels"
        class="mt-0.5 mx-2 mb-0"
      >
        <template v-if="hasSlaPolicyId" #before>
          <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
        </template>
      </CardLabels>
    </div>
  </div>
</template>
